import { type FC, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { DayjsFormats } from 'shared/lib/dayjs'
import { Icon, Tooltip, Typography } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type MessageProps } from 'widgets/MessageCard/ui/types'
import { MessageDetailsModalStore } from 'widgets/MessageCard/ui/MessageDetailsModal'
import { FailedReasonContent } from 'widgets/MessageCard/ui/DateContent/FailedReasonContent'
import styles from './styles.module.scss'
import { TimezoneTooltip } from './TimezoneTooltip'

type IDateContentProps = MessageProps & {
  hideStatus?: boolean
}

export const DateContent: FC<IDateContentProps> = observer(({ hideStatus, ...props }) => {
  const { configStore, authStore } = useEntitiesContext()
  const { message } = props
  const {
    date,
    statusDisplayName,
    sending,
    stop_on_response,
    is_contact_timezone,
    is_schedule,
    isFailed,
    isDelivered,
    isQueued,
    isOutbound,
    isCreated,
    isSent,
    contact_tz,
  } = message

  const displayDate = useMemo(() => {
    if (is_schedule && is_contact_timezone && contact_tz) {
      return dayjs.utc(date).tz(contact_tz)
    }
    return uiStore.dayjs(date)
  }, [date, is_schedule, is_contact_timezone, contact_tz, uiStore.timezone])

  const isMessageDetailsAvailable =
    (configStore.admin || authStore.isAssume) &&
    (isQueued || isSent || isDelivered || isFailed) &&
    (message.type === 'sms' || message.type === 'mms')

  const status = useMemo(() => {
    if (isFailed) {
      return <Icon icon={'close'} fontSize={14} />
    }

    if (isDelivered) {
      return <Icon icon={'check3'} fontSize={14} />
    }

    if (is_schedule) {
      return <Icon icon={'clockFastForward'} fontSize={14} />
    }

    if (isQueued || isSent || isCreated) {
      return <Icon icon={'check4'} fontSize={14} />
    }

    return ''
  }, [isFailed, isDelivered, isQueued, is_schedule, isSent, isCreated])

  return (
    <div data-name={'message-time'} className={classNames(styles.date)}>
      {stop_on_response && (
        <Tooltip label={'Cancel on response'}>
          <Icon icon={'messageX'} fontSize={12} tertiary />
        </Tooltip>
      )}
      {is_contact_timezone && (
        <Tooltip label={<TimezoneTooltip message={props.message} />}>
          <Icon icon={'userFilled'} fontSize={12} tertiary />
        </Tooltip>
      )}
      <Tooltip
        label={
          <>
            <Typography variant={'body-md-regular'} tag={'div'} ariaLabel={'Status'}>
              {statusDisplayName}
            </Typography>
            <Typography
              variant={'body-sm-regular'}
              tag={'div'}
              ariaLabel={'DayjsFormats'}
              color={'var(--content-primary-tertiary-inverted)'}
            >
              {displayDate.format(DayjsFormats.full3)}
            </Typography>
            {isFailed && <FailedReasonContent message={message} />}
            {isMessageDetailsAvailable && (
              <Typography
                tag={'div'}
                variant={'body-md-medium'}
                onClick={() => new MessageDetailsModalStore(message).openModal()}
                linkLight
                ariaLabel={'SeeDetails'}
              >
                See details
              </Typography>
            )}
          </>
        }
      >
        <Typography variant={'text-input-helper'} ariaLabel={'Sending'} className={styles.time}>
          {sending ? (
            'Sending...'
          ) : (
            <div className={styles.wrap}>
              {displayDate.format(DayjsFormats.time)}
              {!hideStatus && isOutbound && (
                <div
                  className={classNames(styles.status, {
                    [styles.failed]: isFailed,
                    [styles.delivered]: isDelivered,
                  })}
                >
                  {status}
                </div>
              )}
            </div>
          )}
        </Typography>
      </Tooltip>
    </div>
  )
})
